<template>
  <div class="bg-item-card appeal-item-card" :class="{ 'bg-item-card-error': hasError }">
    <div class="bg-item-top">
      <span class="bg-seq" v-if="item.sequencialItem">{{ item.sequencialItem }}</span>
      <span class="bg-pill bg-pill-red" v-else>Guia inteira</span>
      <span class="bg-proc">
        <code>{{ item.code }}</code><b>{{ item.description }}</b>
      </span>
      <span class="text-xs text-gray" v-if="item.meta">{{ item.meta }}</span>
      <gloss-code-pill :code="item.glossCode" :description="item.glossDescription"/>
      <div class="bg-item-vals">
        <div><small>Faturado</small><span>{{ item.informed | currency }}</span></div>
        <div>
          <small>Glosado</small><span class="text-red">−{{ item.glossed | currency }}</span>
        </div>
        <div v-if="item.recovered">
          <small>Recuperado</small><span class="text-green">+{{ item.recovered | currency }}</span>
        </div>
      </div>
    </div>

    <div class="bg-item-fields" v-if="!readonly">
      <div class="form-group" :class="{'has-error': validator.appealedValue.$error}">
        <label class="form-label">Valor recursado</label>
        <div class="input-group">
          <span class="input-group-addon addon-sm">R$</span>
          <dx-input-number :id="`appeal-value-${item.glossId}`" v-model="item.appealedValue"
                           class="form-input text-right" :precision="2" maxlength="10"
                           @blur="validator.appealedValue.$touch()" @input="changed"/>
        </div>
        <template v-if="validator.appealedValue.$error">
          <div class="form-input-hint"
               v-if="!validator.appealedValue.minValue || !validator.appealedValue.required">
            Informe um valor
          </div>
          <div class="form-input-hint" v-else-if="!validator.appealedValue.maxValue">
            Maior que o valor glosado ({{ item.openValue | currency }})
          </div>
        </template>
        <div class="text-xs text-gray" v-else>
          máx. {{ item.openValue | currency }}
          <button class="btn btn-link btn-sm bg-inline-link" v-if="item.appealedValue !==
          item.openValue"
                  @click="appealAll">· Recursar tudo</button>
        </div>
      </div>
      <div class="form-group" :class="{'has-error': validator.justification.$error}">
        <div class="bg-just-head">
          <label class="form-label">Justificativa</label>
          <justification-picker
            :code="item.glossCode"
            :context="context"
            :items="justifications"
            :loading="loadingJustifications"
            @open="$emit('load-justifications', item.glossCode)"
            @select="useJustification"
            @remove="(entry) => $emit('remove-justification', entry)"
          />
        </div>
        <textarea class="form-input" rows="3" v-model="item.justification" :maxlength="maxLength"
                  placeholder="Explique por que a glosa não procede (até 500 caracteres)"
                  @blur="validator.justification.$touch()" @input="changed"></textarea>
        <div class="form-input-hint" v-if="validator.justification.$error">Campo obrigatório</div>
        <div class="bg-item-foot">
          <label class="form-checkbox bg-save-check">
            <input type="checkbox" v-model="item.saveJustification" @change="changed">
            <i class="form-icon"></i>Salvar esta justificativa para o código {{ item.glossCode }}
          </label>
          <input type="text" class="form-input input-sm bg-save-title" v-if="item.saveJustification"
                 v-model="item.saveTitle" maxlength="120" placeholder="Título da justificativa"
                 @input="changed">
          <button class="btn btn-link btn-sm bg-inline-link"
                  v-if="sameCodeCount > 0 && item.justification" @click="applyToCode">
            Aplicar a todos os itens com {{ item.glossCode }} ({{ sameCodeCount }})
          </button>
          <span class="bg-counter" :class="{ warn: length >= 480 }">
            {{ length }}/{{ maxLength }}
          </span>
        </div>
      </div>
    </div>
    <div class="bg-item-read" v-else>
      <div class="bg-item-read-row">
        <span class="text-gray">Valor recursado</span>
        <b class="tnum">{{ item.appealedValue | currency }}</b>
      </div>
      <div class="bg-item-read-row">
        <span class="text-gray">Justificativa</span>
        <span class="bg-item-read-text">{{ item.justification || '—' }}</span>
      </div>
      <div class="bg-item-read-row" v-if="item.response && item.response.respondedAt">
        <span class="text-gray">Resposta</span>
        <span>
          <span class="bg-pill" :class="responseClass">{{ responseLabel }}</span>
          <span class="tnum ml-1">{{ item.response.acceptedValue | currency }} acatados</span>
          <span class="text-xs text-gray" v-if="item.response.operatorJustification">
            · {{ item.response.operatorJustification }}
          </span>
        </span>
      </div>
    </div>
    <div class="bg-item-remove" v-if="!readonly && removable">
      <button class="btn btn-link btn-sm text-error" @click="$emit('remove')">
        Remover do recurso
      </button>
    </div>
  </div>
</template>

<script>
import GlossCodePill from './GlossCodePill.vue';
import JustificationPicker from './JustificationPicker.vue';
import { num, round2 } from '../helpers';

export default {
  components: {
    GlossCodePill,
    JustificationPicker,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
    justifications: {
      type: Array,
      default: () => [],
    },
    loadingJustifications: {
      type: Boolean,
      default: false,
    },
    sameCodeCount: {
      type: Number,
      default: 0,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: true,
    },
    maxLength: {
      type: Number,
      default: 500,
    },
  },
  computed: {
    length() {
      return (this.item.justification || '').length;
    },
    hasError() {
      return this.validator.$error;
    },
    context() {
      return this.item.context || {};
    },
    responseLabel() {
      const accepted = num(this.item.response && this.item.response.acceptedValue);
      if (accepted <= 0) return 'Mantido';
      if (round2(accepted) >= round2(num(this.item.appealedValue))) return 'Acatado';
      return 'Parcial';
    },
    responseClass() {
      if (this.responseLabel === 'Acatado') return 'bg-pill-green';
      if (this.responseLabel === 'Parcial') return 'bg-pill-green-amber';
      return 'bg-pill-red-strong';
    },
  },
  methods: {
    changed() {
      this.$emit('input', this.item);
    },
    appealAll() {
      this.item.appealedValue = this.item.openValue;
      this.changed();
    },
    applyToCode() {
      this.$emit('apply-to-code', {
        code: this.item.glossCode,
        text: this.item.justification,
        justificationId: this.item.justificationId,
      });
    },
    useJustification({ id, text }) {
      this.item.justification = text;
      this.item.justificationId = id;
      this.validator.justification.$touch();
      this.changed();
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.appeal-item-card {
  .bg-just-head {
    align-items: center;
    display: flex;
    gap: $layout-spacing-sm;
    justify-content: space-between;
    margin-bottom: .1rem;
    .form-label { padding-bottom: 0; }
  }
  .bg-inline-link { padding: 0; }
  .bg-save-check { font-size: $font-size-xs; margin: 0; min-height: 1rem; }
  .bg-save-title { max-width: 12rem; }
  .bg-item-read {
    display: flex;
    flex-direction: column;
    font-size: $font-size-sm;
    gap: .2rem;
    margin-top: $layout-spacing-sm;
    .bg-item-read-row {
      display: grid;
      gap: $layout-spacing;
      grid-template-columns: 7rem minmax(0, 1fr);
    }
    .bg-item-read-text { white-space: pre-line; }
  }
  .bg-item-remove { margin-top: $layout-spacing-sm; .btn { padding: 0; } }
}
</style>
